import React, { useState, useRef, useEffect, useMemo } from 'react'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import { Dialog } from 'primereact/dialog'
import useSession from '@aytm/app/swidget/base/session/useSession.ts'
import useTheme from '@aytm/app/swidget/base/modules/theme/useTheme.ts'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline.ts'
import Header from './Components/Header.tsx'
import Footer from './Components/Footer.tsx'
import Main from './Components/Main.tsx'
import Cart from './Components/Cart.tsx'
import Product from './Components/Product.tsx'
import ExitSimulation from '../../_common/ExitSimulation.tsx'
import { IEcommerceQuestionViewProps } from '../types.ts'
import useLoadScriptInFrame from '@aytm/app/swidget/base/questions/Ecommerce/hooks/useLoadScriptInFrame.ts'
import useScrollMemory from '@aytm/app/swidget/base/questions/Ecommerce/hooks/useScrollMemory.ts'
import style from './Walmart.module.scss'
import './assets/iconset/walmart_iconset.css'

const WalmartView = ({ schema, response, checkout, setClosed, onUpdateResponse }: IEcommerceQuestionViewProps) => {
  const session = useSession()
  const na = useMemo(() => schema.answers.find(answer => answer.na), [schema])
  const totals = [
    response.answers.reduce((prev, cur) => prev + (cur.id === na?.id ? 0 : cur.v), 0),
    response.answers.reduce((prev, cur) => prev + cur.v * cur.price, 0)
  ]
  const selectedIndex = response.answers.findIndex(r => r.selected)
  const timeline = useTimeline()
  const findability = 'find' == schema.mode_type || (1 == schema.max_cart_size && !schema.shopping_cart)
  const theme = useTheme()
  const pvRoot: HTMLElement = document.querySelector('.surveyWidgetFullsizeContainer')
  const [showCart, setShowCart] = useState<boolean>(false)
  const deliveryDates = useRef(
    response.answers.map(_ => new Date().setDate(new Date().getDate() + Math.floor(Math.random() * 5)))
  )
  const viewport = useViewport()
  const [needHint, setNeedHint] = useState(true)
  const {
    containerRef,
    registerCardRef,
    findCenterElement,
    restorePosition,
    scrollTop,
    resetCenterElement: resetCenterElementRef
  } = useScrollMemory()
  useLoadScriptInFrame('https://unpkg.com/@google/model-viewer', containerRef, viewport.mobile && viewport.emulated)

  useEffect(() => {
    if (!showCart && selectedIndex < 0) {
      restorePosition()
    }
  }, [showCart, selectedIndex, restorePosition, viewport.orientation])

  const clearSelection = () => {
    if (showCart) {
      setShowCart(false)
      timeline.log({
        type: 'close_cart'
      })
    }
    if (selectedIndex >= 0) {
      timeline.log({
        type: 'close_popup',
        ref: response.answers[selectedIndex].id
      })
    }
    onUpdateResponse({
      answers: response.answers.map(r => {
        return { ...r, selected: false }
      }),
      clicked: response.clicked
    })
  }

  const onItemSelect = (index: number, bannerPlacement?: string) => {
    if (bannerPlacement) {
      timeline.log({
        type: 'banner_click',
        ref: response.answers[index].id,
        context: { placement: bannerPlacement }
      })
    }
    if (selectedIndex >= 0) response.answers[selectedIndex].selected = null
    if (showCart) {
      setShowCart(false)
      timeline.log({
        type: 'close_cart'
      })
    }
    response.answers[index].selected = true
    onUpdateResponse(response)
    timeline.log({
      target: '0,0',
      type: 'open_popup',
      ref: response.answers[index].id
    })
  }

  const onItemClose = () => {
    if (selectedIndex >= 0) {
      response.answers[selectedIndex].selected = false
      onUpdateResponse(response)
      timeline.log({
        type: 'close_popup',
        ref: response.answers[selectedIndex].id
      })
    }
  }

  const onCartOpen = () => {
    onItemClose()
    timeline.log({
      type: 'open_cart'
    })
    setShowCart(true)
  }

  const onCartClose = () => {
    setShowCart(false)
    timeline.log({
      type: 'close_cart'
    })
  }

  const onAddToCart = (qnt: 1 | -1, index: number) => {
    response.answers[index].v += qnt
    if (selectedIndex < 0 && !showCart) {
      timeline.log({
        target: '0,0',
        type: 'open_popup',
        ref: response.answers[index].id
      })
    }
    timeline.log({
      type: showCart ? 'edit_in_cart' : 'add_to_cart',
      ref: response.answers[index].id,
      target: showCart || selectedIndex >= 0 ? response.answers[index].v.toString() : '1'
    })
    if ((!showCart && selectedIndex < 0) || (findability && selectedIndex >= 0)) {
      timeline.log({
        type: 'close_popup',
        ref: response.answers[index].id
      })
    }
    onUpdateResponse(response)
  }

  const onBuyNow = (index: number) => {
    // if (checkLimits(1, response.answers[index].price, response.answers[index].v, totals, schema)) {
    response.answers[index].v += 1
    timeline.log({
      type: 'add_to_cart',
      ref: response.answers[index].id,
      target: response.answers[index].v.toString()
    })
    // }
    if (!findability) {
      response.answers[index].selected = false
    }
    timeline.log({
      type: 'close_popup',
      ref: response.answers[index].id
    })
    if (!findability) {
      setShowCart(true)
      timeline.log({
        type: 'open_cart'
      })
    }
    timeline.log({
      type: 'buy_now',
      ref: response.answers[index].id,
      target: '1'
    })
    onUpdateResponse(response)
  }

  useEffect(() => {
    if (na) {
      const naResponse = response.answers.find(answer => answer.id === na.id)
      if (naResponse.v > 0) {
        checkout()
      }
    }
  }, [response, checkout, na])

  if (theme.style?.pv && !theme.style?.flutter && !pvRoot) return null

  return (
    <Dialog
      maskClassName={`${style.dialog} ${session.themeClassNames}`}
      appendTo={theme.style?.pv && pvRoot ? pvRoot : session.document?.body}
      onHide={() => {}}
      maximizable={true}
      maximized={true}
      visible={true}
    >
      <div
        className={`${style.container} ${viewport.mobile ? style.containerMobile : ''}`}
        onScroll={findCenterElement}
        ref={containerRef}
      >
        <Header
          selectedIndex={selectedIndex}
          totals={totals}
          schema={schema}
          findability={findability}
          showCart={showCart}
          onCartOpen={onCartOpen}
          onCartClose={onCartClose}
          onItemClose={onItemClose}
          clearSelection={clearSelection}
        />
        {selectedIndex >= 0 ? (
          <Product
            // AYTM-12710 OQ-PF-32: key by the selected variant id so switching variants remounts the
            // detail view, resetting the gallery to the TARGET variant's default (matches Amazon).
            key={schema.answers[selectedIndex].id}
            answer={schema.answers[selectedIndex]}
            schema={schema}
            quantity={response.answers[selectedIndex].v}
            index={selectedIndex}
            deliveryDate={deliveryDates.current[selectedIndex]}
            findability={findability}
            onAddToCart={onAddToCart}
            onBuyNow={onBuyNow}
            onItemSelect={onItemSelect}
            onItemClose={onItemClose}
            scrollTop={scrollTop}
          />
        ) : showCart ? (
          <Cart
            response={response}
            schema={schema}
            totals={totals}
            deliveryDates={deliveryDates.current}
            onAddToCart={onAddToCart}
            onCartClose={onCartClose}
            onItemSelect={onItemSelect}
            checkout={checkout}
            onUpdateResponse={onUpdateResponse}
            scrollTop={scrollTop}
          />
        ) : (
          <Main
            schema={schema}
            response={response}
            deliveryDates={deliveryDates.current}
            findability={findability}
            onAddToCart={onAddToCart}
            onItemSelect={onItemSelect}
            registerCardRef={registerCardRef}
            resetCenterElementRef={resetCenterElementRef}
          />
        )}

        {!showCart && (
          <Footer
            showCart={showCart}
            onCartClose={onCartClose}
            onItemClose={onItemClose}
            showHint={() => {
              setNeedHint(true)
            }}
            closeSimulation={setClosed}
          />
        )}
      </div>
      <ExitSimulation
        setClosed={setClosed}
        needExternalHint={needHint}
        setExternalHint={viewport.mobile ? setNeedHint : null}
        hintText={schema.instruction}
      />
    </Dialog>
  )
}

export default WalmartView
